/**
 * GNK(굿네이버스) 예산과목 시드
 * - 관-항-세항 + 세항-목-세목 엑셀을 세항코드 기준 매핑
 * - flat 행: levelName + subjectCode + parentCode (레벨 통합 관리)
 *
 * 데이터 재생성: node scripts/buildGnkBudgetSubjectSeed.js
 */
import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  clonePreset,
  validateStructure,
  getConfirmedStructure,
  loadStructurePayload,
  saveStructurePayload,
  loadSubjects,
  saveSubjects,
  STRUCTURE_BUDGET_TYPE,
  resolveBudgetTypes,
} from '../budgetStructureStore';
import { ensureSubjectsIdentity } from '../budgetSubjectHistoryStore';
import { GNK_BUDGET_SUBJECT_ROWS } from './gnkBudgetSubjects.data';

export const GNK_BUDGET_SUBJECT_SOURCE =
  'GNK 예산과목 (관-항-세항 + 세항-목-세목 엑셀 매핑)';

export const GNK_TENANT_CODE = 'GNK';

export function isGnkTenant(tenantCode) {
  return String(tenantCode || '').trim().toUpperCase() === GNK_TENANT_CODE;
}

/** MOFAS 시드·데모 등 GNK 전용 데이터로 교체가 필요한지 */
export function needsGnkSubjectReseed(subjects = []) {
  if (!subjects.length) return true;
  if (subjects.some((r) => String(r.remark || '').includes('GNK 예산과목'))) return false;
  if (subjects.some((r) => String(r.remark || '').includes('행안부'))) return true;
  if (subjects.some((r) => r.subjectCode === '700' || r.subjectCode === '600')) return true;
  if (subjects.length <= 8 && subjects.some((r) => r.subjectCode === '100')) return true;
  return false;
}

export function getGnkBudgetSubjectSeed(budgetType = STRUCTURE_BUDGET_TYPE) {
  const bt = budgetType || STRUCTURE_BUDGET_TYPE;
  return ensureSubjectsIdentity(
    GNK_BUDGET_SUBJECT_ROWS.map((r) => ({
      ...r,
      budgetType: bt,
    }))
  );
}

/**
 * GNK 테넌트 · 지출 · 사업예산 시드 부트스트랩
 */
export async function ensureGnkBudgetBootstrap(dims, { userId } = {}) {
  const types = resolveBudgetTypes(dims.budgetType);
  let seeded = false;
  let seedCount = 0;
  const allSubjects = [];

  const structureDims = {
    tenantCode: dims.tenantCode,
    fiscalYear: dims.fiscalYear,
    inoutDiv: dims.inoutDiv,
    purpose: dims.purpose || '집행',
  };

  let confirmed = getConfirmedStructure(structureDims);
  if (!confirmed) {
    const rows = clonePreset(structureDims.inoutDiv, structureDims.purpose);
    const v = validateStructure(rows);
    if (!v.canConfirm) {
      return { ok: false, reason: 'GNK 시드 구조를 확정할 수 없습니다.', seedCount: 0 };
    }
    const fy = String(structureDims.fiscalYear || new Date().getFullYear());
    const prev = await loadStructurePayload(structureDims);
    await saveStructurePayload(structureDims, {
      dims: structureDims,
      rows,
      status: '확정',
      purpose: structureDims.purpose,
      usedLevels: v.used,
      finalLevelName: v.finalLevelName,
      acctMapLevelNames: v.acctMapLevelNames,
      previewText: v.previewText,
      versionNo: (Number(prev?.versionNo) || 0) + 1 || 1,
      confirmedAt: new Date().toISOString(),
      confirmedBy: userId || 'GNK-SUBJECT-SEED',
      startDate: `${fy}-01-01`,
      endDate: `${fy}-12-31`,
      remark: GNK_BUDGET_SUBJECT_SOURCE,
      savedAt: new Date().toISOString(),
      savedBy: userId || 'GNK-SUBJECT-SEED',
    });
    confirmed = getConfirmedStructure(structureDims);
  }

  if (!confirmed) {
    return { ok: false, reason: 'GNK 시드 구조를 확정할 수 없습니다.', seedCount: 0 };
  }

  const useGnkExpenseSeed = structureDims.inoutDiv === '지출';

  for (const budgetType of types) {
    const d = { ...dims, budgetType, purpose: structureDims.purpose };
    let subjects = await loadSubjects(d);

    if (useGnkExpenseSeed && budgetType === STRUCTURE_BUDGET_TYPE) {
      // API: 이미 과목이 있으면 화면 오픈 시 시드 INSERT 하지 않음
      if (!isBdApiEnabled() && needsGnkSubjectReseed(subjects)) {
        subjects = getGnkBudgetSubjectSeed(budgetType);
        await saveSubjects(d, subjects);
        seeded = true;
      }
    }

    seedCount += subjects.length;
    subjects.forEach((r) => allSubjects.push({ ...r, budgetType: r.budgetType || budgetType }));
  }

  return {
    ok: true,
    structure: confirmed,
    subjects: allSubjects,
    seedCount,
    seeded,
    source: GNK_BUDGET_SUBJECT_SOURCE,
  };
}
